import { useCallback } from 'react';
import type { ChildResult } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { mergeChildResults } from '@atlassian/jira-command-palette/src/common/utils.tsx';
import { useNavigationViewPlansPermission } from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { QuickSearchResponseData } from '../../../../common/types.tsx';
import { useGetErrorChildResult } from '../../../../common/ui/error/index.tsx';
import { useGetRecommendedChildResult } from '../../../../common/ui/recommended/index.tsx';
import { useSetTimeoutAutoClear } from '../../../../common/utils/use-set-timeout-auto-clear/index.tsx';
import { transformResultsForAnalytics } from '../../../../controllers/use-quick-search-analytics/utils.tsx';
import { useGetRecentBoardsChildResult } from './boards/index.tsx';
import { useGetRecentFiltersChildResult } from './filters/index.tsx';
import { useGetRecentIssuesChildResult } from './issues/index.tsx';
import { useGetRecentPlansChildResult } from './plans/index.tsx';
import { useGetRecentProjectsChildResult } from './projects/index.tsx';

export const useGetRecentResults = () => {
	const { getChildResult: getRecentBoardsChildResult } = useGetRecentBoardsChildResult();
	const { getChildResult: getRecentIssuesChildResult } = useGetRecentIssuesChildResult();
	const { getChildResult: getRecentProjectsChildResult } = useGetRecentProjectsChildResult();
	const { getChildResult: getRecentFiltersChildResult } = useGetRecentFiltersChildResult();
	const { getChildResult: getRecentPlansChildResult } = useGetRecentPlansChildResult();
	const { getChildResult: getErrorChildResult } = useGetErrorChildResult();
	const { getChildResult: getRecommendedChildResult } = useGetRecommendedChildResult();

	const [hasEditOrViewPermissions] = useNavigationViewPlansPermission();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { setTimeoutAutoClear } = useSetTimeoutAutoClear();

	const setTimeoutFunc = setTimeoutAutoClear;

	const getRecentResults = useCallback(
		({
			search,
			quickSearchResponseData,
			error,
		}: {
			search: string;
			quickSearchResponseData?: QuickSearchResponseData;
			error?: Error;
		}): ChildResult => {
			setTimeoutFunc(() => {
				fireUIAnalytics(createAnalyticsEvent({}), 'searchResults shown', 'preQuerySearchResults', {
					...transformResultsForAnalytics(quickSearchResponseData, true),
				});
			}, 0);

			return mergeChildResults([
				getErrorChildResult({ search, quickSearchResponseData, error }),
				getRecommendedChildResult({ search, quickSearchResponseData, error }),
				getRecentIssuesChildResult({ search, quickSearchResponseData, error }),
				getRecentProjectsChildResult({ search, quickSearchResponseData, error }),
				getRecentBoardsChildResult({ search, quickSearchResponseData, error }),
				getRecentFiltersChildResult({ search, quickSearchResponseData, error }),
				...(hasEditOrViewPermissions
					? [getRecentPlansChildResult({ search, quickSearchResponseData, error })]
					: []),
			]);
		},
		[
			createAnalyticsEvent,
			getErrorChildResult,
			getRecommendedChildResult,
			getRecentIssuesChildResult,
			getRecentProjectsChildResult,
			getRecentBoardsChildResult,
			getRecentFiltersChildResult,
			hasEditOrViewPermissions,
			getRecentPlansChildResult,
			setTimeoutFunc,
		],
	);

	return getRecentResults;
};
